import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { formatMoney } from '@johnsaks/core'
import { useEffect, useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import {
  BtnGhost,
  BtnPrimary,
  Field,
  Select,
  TextArea,
  TextInput,
} from '@/components/field'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate, type ProductDetail } from '@/lib/api'
import { centsFromDollars, dollarsFromCents, PRODUCT_STATUS } from '@/lib/labels'

type CategoryOpt = { id: string; slug: string; name: string }

type FormState = {
  name: string
  sku: string
  slug: string
  description: string
  priceDollars: string
  comparePriceDollars: string
  categoryId: string
  powered: string
  weightGrams: string
  packaging: string
  supplier: string
  leadTimeDays: string
}

function blankForm(): FormState {
  return {
    name: '',
    sku: '',
    slug: '',
    description: '',
    priceDollars: '0.00',
    comparePriceDollars: '',
    categoryId: '',
    powered: '',
    weightGrams: '',
    packaging: '',
    supplier: '',
    leadTimeDays: '',
  }
}

function fromProduct(p: ProductDetail): FormState {
  return {
    name: p.name,
    sku: p.sku,
    slug: p.slug,
    description: p.description,
    priceDollars: dollarsFromCents(p.priceCents),
    comparePriceDollars:
      p.comparePriceCents != null ? dollarsFromCents(p.comparePriceCents) : '',
    categoryId: p.category.id ?? '',
    powered: p.powered ?? '',
    weightGrams: p.weightGrams != null ? String(p.weightGrams) : '',
    packaging: p.packaging ?? '',
    supplier: p.supplier ?? '',
    leadTimeDays: p.leadTimeDays != null ? String(p.leadTimeDays) : '',
  }
}

export function ProduitDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const toast = useToast()
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState>(blankForm())
  const [archiveOpen, setArchiveOpen] = useState(false)

  const product = useQuery({
    queryKey: ['admin-product', id],
    queryFn: () => api<ProductDetail>(`/admin/products/${id}`),
    enabled: Boolean(id),
  })
  const categories = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => api<{ items: CategoryOpt[] }>('/admin/categories'),
  })

  useEffect(() => {
    if (product.data) setForm(fromProduct(product.data))
  }, [product.data])

  const save = useMutation({
    mutationFn: () => {
      const compareRaw = form.comparePriceDollars.trim()
      const weightRaw = form.weightGrams.trim()
      const leadRaw = form.leadTimeDays.trim()
      return apiMutate<ProductDetail>(`/admin/products/${id}`, 'PATCH', {
        name: form.name,
        sku: form.sku,
        slug: form.slug,
        description: form.description,
        priceCents: centsFromDollars(form.priceDollars),
        comparePriceCents: compareRaw ? centsFromDollars(compareRaw) : null,
        categoryId: form.categoryId,
        powered: form.powered.trim() || null,
        weightGrams: weightRaw ? Number.parseInt(weightRaw, 10) : null,
        packaging: form.packaging.trim() || null,
        supplier: form.supplier.trim() || null,
        leadTimeDays: leadRaw ? Number.parseInt(leadRaw, 10) : null,
      })
    },
    onSuccess: () => {
      toast.ok('Produit enregistré')
      void qc.invalidateQueries({ queryKey: ['admin-product', id] })
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const publish = useMutation({
    mutationFn: () => apiMutate(`/admin/products/${id}/publish`, 'POST'),
    onSuccess: () => {
      toast.ok('Produit publié')
      void qc.invalidateQueries({ queryKey: ['admin-product', id] })
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const unpublish = useMutation({
    mutationFn: () => apiMutate(`/admin/products/${id}/unpublish`, 'POST'),
    onSuccess: () => {
      toast.ok('Produit remis en brouillon')
      void qc.invalidateQueries({ queryKey: ['admin-product', id] })
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const archive = useMutation({
    mutationFn: () => apiMutate(`/admin/products/${id}`, 'DELETE'),
    onSuccess: () => {
      toast.ok('Produit archivé')
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
      window.location.href = '/produits'
    },
    onError: (e: Error) => toast.err(e.message),
  })

  if (product.isError) {
    return (
      <div className="carte-admin p-6">
        <p className="text-rouge-texte">Produit introuvable.</p>
        <a href="/produits" className="mt-3 inline-block text-sm text-rouge">
          Retour à la liste
        </a>
      </div>
    )
  }

  if (product.isLoading || !product.data) {
    return <p className="text-second-admin">Chargement…</p>
  }

  const p = product.data
  const st = PRODUCT_STATUS[p.status ?? 'DRAFT'] ?? {
    label: p.status ?? 'Brouillon',
    tone: 'draft',
  }
  const set = (key: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [key]: value }))

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <a href="/produits" className="kicker text-second-admin hover:text-encre-admin">
          ← Produits
        </a>
        <StatusPill label={st.label} tone={st.tone} />
        <div className="ml-auto flex flex-wrap gap-2">
          {p.status === 'PUBLISHED' ? (
            <BtnGhost disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>
              Dépublier
            </BtnGhost>
          ) : (
            <BtnPrimary disabled={publish.isPending} onClick={() => publish.mutate()}>
              Publier
            </BtnPrimary>
          )}
          <BtnGhost onClick={() => setArchiveOpen(true)}>Archiver</BtnGhost>
          <BtnPrimary disabled={save.isPending} onClick={() => save.mutate()}>
            Enregistrer
          </BtnPrimary>
        </div>
      </div>

      <section className="carte-admin grid gap-4 p-5 sm:grid-cols-2">
        <Field label="Nom">
          <TextInput value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="SKU">
          <TextInput value={form.sku} onChange={(e) => set('sku', e.target.value)} />
        </Field>
        <Field label="Slug">
          <TextInput value={form.slug} onChange={(e) => set('slug', e.target.value)} />
        </Field>
        <Field label="Prix (USD)">
          <TextInput
            type="number"
            step="0.01"
            min="0"
            value={form.priceDollars}
            onChange={(e) => set('priceDollars', e.target.value)}
          />
        </Field>
        <Field label="Prix barré (USD)" hint="Laisser vide pour aucun prix comparé.">
          <TextInput
            type="number"
            step="0.01"
            min="0"
            value={form.comparePriceDollars}
            onChange={(e) => set('comparePriceDollars', e.target.value)}
          />
        </Field>
        <Field label="Catégorie">
          <Select
            value={form.categoryId}
            onChange={(e) => set('categoryId', e.target.value)}
          >
            <option value="">Choisir…</option>
            {(categories.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Alimentation / powered">
          <TextInput
            value={form.powered}
            onChange={(e) => set('powered', e.target.value)}
            placeholder="ex. USB-C"
          />
        </Field>
        <Field label="Poids (g)">
          <TextInput
            type="number"
            min="0"
            value={form.weightGrams}
            onChange={(e) => set('weightGrams', e.target.value)}
          />
        </Field>
        <Field label="Emballage">
          <TextInput
            value={form.packaging}
            onChange={(e) => set('packaging', e.target.value)}
          />
        </Field>
        <Field label="Fournisseur">
          <TextInput
            value={form.supplier}
            onChange={(e) => set('supplier', e.target.value)}
          />
        </Field>
        <Field label="Délai (jours)">
          <TextInput
            type="number"
            min="0"
            value={form.leadTimeDays}
            onChange={(e) => set('leadTimeDays', e.target.value)}
          />
        </Field>
        <Field label="Prix actuel">
          <p className="py-2 font-titre text-lg text-encre-admin">
            {formatMoney(centsFromDollars(form.priceDollars))}
          </p>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Description">
            <TextArea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={5}
            />
          </Field>
        </div>
      </section>

      {p.variants?.length ? (
        <section className="carte-admin">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-bordure-admin px-4 py-3">
            <h2 className="text-base">Variantes & stock</h2>
            <a href="/stocks" className="text-xs text-rouge uppercase tracking-[0.12em]">
              Ajuster
            </a>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['SKU', 'Libellé', 'En main', 'Réservé', 'Dispo', 'Prix', 'Défaut'].map((h) => (
                  <th key={h} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {p.variants.map((v) => (
                <tr key={v.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5">{v.sku}</td>
                  <td className="px-4 py-2.5">{v.label ?? v.color}</td>
                  <td className="px-4 py-2.5 tabular-nums">{v.onHand ?? v.stock}</td>
                  <td className="px-4 py-2.5 tabular-nums">{v.reserved ?? 0}</td>
                  <td className="px-4 py-2.5 tabular-nums">
                    {v.available ?? Math.max((v.onHand ?? v.stock) - (v.reserved ?? 0), 0)}
                  </td>
                  <td className="px-4 py-2.5">
                    {v.priceCents != null ? formatMoney(v.priceCents) : '—'}
                  </td>
                  <td className="px-4 py-2.5">{v.isDefault ? 'Oui' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {p.inventory ? (
        <section className="carte-admin p-4">
          <h2 className="text-base">Stock (rollup produit)</h2>
          <p className="mt-2 text-sm text-second-admin">
            Dispo {p.inventory.onHand - p.inventory.reserved} · en main {p.inventory.onHand} ·
            réservé {p.inventory.reserved} · seuil {p.inventory.threshold}
          </p>
        </section>
      ) : null}

      <ConfirmDialog
        open={archiveOpen}
        title="Archiver ce produit ?"
        body="Le produit passera en statut archivé et ne sera plus visible sur la boutique."
        confirmLabel="Archiver"
        danger
        busy={archive.isPending}
        onClose={() => setArchiveOpen(false)}
        onConfirm={() => archive.mutate()}
      />
    </div>
  )
}
